/**
 * Atmospheric backdrop. Stars are placed on a large sphere shell far outside
 * the orbit-controls maxDistance, and rendered with sizeAttenuation OFF — each
 * point is a fixed 1.5px pixel regardless of distance, so stars are always
 * tiny background pinpricks instead of camera-near squares.
 *
 * Why this matters: with sizeAttenuation enabled, particles between the camera
 * and the network rendered as chunky foreground squares. The user explicitly
 * called this out as visually broken. Pixel-sized + far-away = unmistakably
 * background.
 *
 * Pure decoration; raycast disabled so it never intercepts clicks.
 */
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { STAR_COLOR, STAR_ALPHA } from './palette';
import { seededRandom, spherePoint } from '../graph/rng';

const STAR_COUNT = 1400;
// 320 is well beyond OrbitControls maxDistance (180), so the camera can
// never get close enough to a star for it to feel like foreground.
const FIELD_RADIUS = 320;
/** Separate from LAYOUT_SEED so the sky and the map re-roll independently. */
const STARFIELD_SEED = 0x57a25;
const ROT_SPEED = 0.005;

export function Starfield() {
  const groupRef = useRef<THREE.Group>(null!);

  // Seeded, like the layout. A fixed sky is not just tidiness: the stars
  // are the only fixed reference in the scene, so if they move between
  // loads then two screenshots of the same graph differ in the one
  // element that could have told you the camera had moved. Its own seed
  // so that re-rolling the map does not also re-roll the sky.
  const positions = useMemo(() => {
    const rand = seededRandom(STARFIELD_SEED);
    const positions = new Float32Array(STAR_COUNT * 3);
    for (let i = 0; i < STAR_COUNT; i++) {
      // Uniform on the sphere, with mild radial jitter for thickness.
      const r = FIELD_RADIUS * (0.85 + rand() * 0.15);
      const [x, y, z] = spherePoint(rand, r);
      positions[i * 3 + 0] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
    }
    return positions;
  }, []);

  useFrame((_, dt) => {
    if (groupRef.current) groupRef.current.rotation.y += dt * ROT_SPEED;
  });

  return (
    <group ref={groupRef}>
      <points raycast={() => null}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[positions, 3]}
            count={STAR_COUNT}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          color={STAR_COLOR}
          // PIXEL size, not world size. With sizeAttenuation: false, three.js
          // interprets `size` as gl_PointSize directly, so each star is
          // exactly N pixels regardless of how close the camera gets.
          size={1.5}
          sizeAttenuation={false}
          transparent
          opacity={STAR_ALPHA}
          // No depth write so the additive contribution can't punch a hole
          // in the depth buffer for the foreground network.
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          toneMapped={false}
        />
      </points>
    </group>
  );
}
